import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ColDef, ColGroupDef, ExcelStyle } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { CalculationsService } from '../../../../../services/calculations/calculations.service';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { formatDate, formatNumber } from '@angular/common';
import { DxsrExcelStyling } from '../dxsr-excel-styling';
import { AgLoadingOverlayComponent } from '../../../../ag-grid-components/ag-loading-overlay/ag-loading-overlay.component';
import { DxSummaryLineItem } from '../../../../../models/dx-summary-line-item';
import { PayerItem } from '../../../../../models/payer-item';
import { AgGridExportHelper, IExportableColDef, IQueryFilterRow } from '../../../../../helpers/ag-grid-export-helper';
import { ClientToastController } from '../../../../../toasts/client-toast.lib';

interface DxSummaryTotals {
  dxCount: number;
  hccCount: number;
}

interface DxEventsSummaryCellParams {
  data?: DxEventsSummaryReportRowItem;
  value?: number | string | null;
}

interface DxEventsSummaryClassRuleParams {
  data?: DxEventsSummaryReportRowItem;
}

export interface DxEventsSummaryReportRowItem {
  DisplayName: string;
  HCCs: number;
  HCCPercent: number;
  DX: number;
  DXPercent: number;
  Indent: number;
  StyleKey: string;
}

@Component({
  selector: 'app-dx-events-summary-report',
  imports: [AgGridModule],
  templateUrl: './dx-events-summary-report.component.html',
})
export class DxEventsSummaryReportComponent implements OnInit, OnDestroy {
  private readonly exportErrorToastDurationMs = 5000;

  @Input() serviceYear = 2022;
  @Input() payerIDs: number[] = [];
  @Input() payerOptions: PayerItem[] = [];

  rowItems: DxEventsSummaryReportRowItem[] = [];
  refreshDate: Date | null = null;
  lastGeneratedServiceYear = this.serviceYear;
  lastGeneratedPayerIDs: number[] = [...this.payerIDs];

  columnDefs: (ColDef<DxEventsSummaryReportRowItem> | ColGroupDef<DxEventsSummaryReportRowItem>)[] = [];

  defaultColDef: ColDef = {
    menuTabs: [],
    minWidth: 100,
    autoHeight: true
  };

  loadingOverlayComponent: unknown = AgLoadingOverlayComponent;

  excelStyles: ExcelStyle[] = DxsrExcelStyling.excelStyles.map(style => {
    if (style.id.startsWith('dxsr-values-')) {
      return {
        ...style,
        numberFormat: {
          format: '#,##0'
        },
        alignment: {
          ...style.alignment,
          horizontal: 'Right'
        }
      };
    }

    if (style.id.startsWith('dxsr-percent-')) {
      return {
        ...style,
        numberFormat: {
          format: '#,##0.00%'
        },
        alignment: {
          ...style.alignment,
          horizontal: 'Right'
        }
      };
    }

    return style;
  });

  subs: Subscription[] = [];
  dataSub?: Subscription;
  private agGridExportHelper: AgGridExportHelper;

  @Output() setExportEnable: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  @HostListener('window:resize')
  onResize() {
    this.agGrid?.api?.sizeColumnsToFit();
  }

  constructor(
    private calculationsService: CalculationsService,
    private toastController: ClientToastController,
  ) {
    this.agGridExportHelper = new AgGridExportHelper();
  }

  ngOnInit(): void {
    this.subs.push(this.calculationsService.lastRefreshDate.subscribe(result => {
      this.refreshDate = result;
      this.agGrid?.api?.setGridOption('columnDefs', this.getColDef(this.serviceYear, this.getRefreshDateLabel()));
    }));
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.subs.forEach(s => s.unsubscribe());
  }

  onGridReady() {
    this.updateReport(this.serviceYear, this.payerIDs);
  }

  updateReport(serviceYear: number, payerIDs: number[] = []) {
    this.serviceYear = serviceYear;
    this.payerIDs = payerIDs;
    this.lastGeneratedServiceYear = serviceYear;
    this.lastGeneratedPayerIDs = [...payerIDs];
    this.setExportEnable.emit(false);
    this.agGrid?.api?.setGridOption('loading', true);

    this.calculationsService.getHccSummaryCalculations(serviceYear);

    this.dataSub?.unsubscribe();
    this.dataSub = this.calculationsService.getDxSummary(serviceYear, this.payerIDs).subscribe({
      next: result => {
        this.rowItems = this.buildRowItems(result ?? []);
        this.agGrid?.api?.setGridOption('rowData', this.rowItems);
        this.agGrid?.api?.setGridOption('columnDefs', this.getColDef(this.serviceYear, this.getRefreshDateLabel()));
        this.agGrid?.api?.sizeColumnsToFit();
        this.agGrid?.api?.setGridOption('loading', false);
        this.setExportEnable.emit(true);
      },
      error: () => {
        this.rowItems = [];
        this.agGrid?.api?.setGridOption('rowData', this.rowItems);
        this.agGrid?.api?.setGridOption('loading', false);
        this.setExportEnable.emit(false);
      }
    });
  }

  onExport() {
    try {
      this.agGridExportHelper.onExport(
        'DX_Events_Summary',
        'DX Events Summary',
        this.getQueryFilters(),
        this.agGrid
      );
    } catch (error) {
      this.toastController.displayToast(
        this.getExportErrorMessage(error),
        this.exportErrorToastDurationMs
      );
    }
  }

  private getExportErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim().length > 0) {
      return `Error: ${error.message}`;
    }

    return 'Error: Unable to export the DX Events Summary report.';
  }

  getColDef(serviceYear: number, refreshDate: string): (ColDef<DxEventsSummaryReportRowItem> | ColGroupDef<DxEventsSummaryReportRowItem>)[] {
    return [
      {
        field: 'DisplayName',
        headerName: `Service Year ${serviceYear} - Data Refreshed ${refreshDate}`,
        headerTooltip: `Service Year ${serviceYear} - Data Refreshed ${refreshDate}`,
        tooltip: (params) => params.data?.DisplayName,
        minWidth: 280,
        flex: 1,
        cellRenderer: (params: DxEventsSummaryCellParams) => {
          const container = document.createElement('span');
          container.style.display = 'inline-block';
          container.textContent = params.value == null ? '' : String(params.value);
          return container;
        },
        cellClassRules: this.getStyleKeyClassRules('dxsr-display-name')
      },
      {
        field: 'HCCs',
        headerName: 'HCCs',
        type: 'numericColumn',
        minWidth: 90,
        maxWidth: 110,
        valueFormatter: params => this.getNumberValue(params.value),
        cellClass: 'numberType',
        cellClassRules: this.getStyleKeyClassRules('dxsr-values')
      },
      {
        field: 'HCCPercent',
        headerName: 'HCC %',
        type: 'numericColumn',
        context: { exportNumberFormat: 'percent', exportPercentScale: 'whole' },
        minWidth: 82,
        maxWidth: 100,
        valueGetter: params => params.data?.HCCPercent ?? 0,
        cellRenderer: (params: DxEventsSummaryCellParams) => this.getPercentValue(params.value),
        cellClass: 'percent',
        cellClassRules: this.getStyleKeyClassRules('dxsr-percent')
      } as IExportableColDef<DxEventsSummaryReportRowItem>,
      {
        field: 'DX',
        headerName: 'DX',
        type: 'numericColumn',
        minWidth: 90,
        maxWidth: 110,
        valueFormatter: params => this.getNumberValue(params.value),
        cellClass: 'numberType',
        cellClassRules: this.getStyleKeyClassRules('dxsr-values')
      },
      {
        field: 'DXPercent',
        headerName: 'DX %',
        type: 'numericColumn',
        context: { exportNumberFormat: 'percent', exportPercentScale: 'whole' },
        minWidth: 82,
        maxWidth: 100,
        valueGetter: params => params.data?.DXPercent ?? 0,
        cellRenderer: (params: DxEventsSummaryCellParams) => this.getPercentValue(params.value),
        cellClass: 'percent',
        cellClassRules: this.getStyleKeyClassRules('dxsr-percent')
      } as IExportableColDef<DxEventsSummaryReportRowItem>
    ];
  }

  private getStyleKeyClassRules(prefix: string) {
    return {
      [`${prefix}-1`]: (params: DxEventsSummaryClassRuleParams) => this.hasStyleKey(params, '1'),
      [`${prefix}-2`]: (params: DxEventsSummaryClassRuleParams) => this.hasStyleKey(params, '2'),
      [`${prefix}-3`]: (params: DxEventsSummaryClassRuleParams) => this.hasStyleKey(params, '3'),
      [`${prefix}-4`]: (params: DxEventsSummaryClassRuleParams) => this.hasStyleKey(params, '4'),
      [`${prefix}-5`]: (params: DxEventsSummaryClassRuleParams) => this.hasStyleKey(params, '5')
    };
  }

  private hasStyleKey(params: DxEventsSummaryClassRuleParams, styleKey: string): boolean {
    return (params.data?.StyleKey ?? '4') === styleKey;
  }

  getNumberValue(value: number | string | null | undefined) {
    const numericValue = Number(value ?? 0);

    if (!Number.isFinite(numericValue) || numericValue === 0) {
      return '0';
    }

    return formatNumber(numericValue, 'en').toString();
  }

  getPercentValue(value: number | string | null | undefined) {
    const numericValue = Number(value ?? 0);

    if (!Number.isFinite(numericValue) || numericValue === 0) {
      return '0%';
    }

    return `${formatNumber(numericValue, 'en', '1.0-2')}%`;
  }

  private buildRowItems(items: DxSummaryLineItem[]): DxEventsSummaryReportRowItem[] {
    const totalCounts = this.getTotals(items);
    const rows: DxEventsSummaryReportRowItem[] = [
      this.createRowItem('Documented Dx Events', '1', totalCounts, totalCounts)
    ];

    const stateGroups = this.groupBy(items, item => item.State);

    stateGroups.forEach(([state, stateItems], stateIndex) => {
      const suppressRootCause = this.isRootCauseSuppressedState(state);

      rows.push(this.createRowItem(this.getDisplayLabel(state, 'Unspecified State'), '1', this.getTotals(stateItems), totalCounts));

      const statusGroups = this.groupBy(stateItems, item => item.Status);

      statusGroups.forEach(([status, statusItems], statusIndex) => {
        rows.push(this.createRowItem(this.getDisplayLabel(status, 'Unspecified Status'), '2', this.getTotals(statusItems), totalCounts));

        if (suppressRootCause) {
          return;
        }

        const rootCauseGroups = this.groupBy(statusItems, item => item.RootCause);

        rootCauseGroups.forEach(([rootCause, rootCauseItems], rootCauseIndex) => {
          const isLastRootCause = rootCauseIndex === rootCauseGroups.length - 1;
          const isLastStatus = statusIndex === statusGroups.length - 1;
          const isLastState = stateIndex === stateGroups.length - 1;
          const styleKey = isLastRootCause
            ? (isLastStatus && isLastState ? '5' : '3')
            : '4';

          rows.push(this.createRowItem(
            this.getDisplayLabel(rootCause, 'Unspecified Root Cause'),
            styleKey,
            this.getTotals(rootCauseItems),
            totalCounts
          ));
        });
      });
    });

    return rows;
  }

  private createRowItem(
    displayName: string,
    styleKey: string,
    counts: DxSummaryTotals,
    totalCounts: DxSummaryTotals
  ): DxEventsSummaryReportRowItem {
    return {
      DisplayName: displayName,
      HCCs: counts.hccCount,
      HCCPercent: this.getPercent(counts.hccCount, totalCounts.hccCount),
      DX: counts.dxCount,
      DXPercent: this.getPercent(counts.dxCount, totalCounts.dxCount),
      Indent: 0,
      StyleKey: styleKey
    };
  }

  private getPercent(value: number, total: number): number {
    if (total === 0) {
      return 0;
    }

    return value / total * 100;
  }

  private getRefreshDateLabel(): string {
    return this.refreshDate ? formatDate(this.refreshDate, 'MM/dd/yyyy', 'en') : '';
  }

  private groupBy(items: DxSummaryLineItem[], selector: (item: DxSummaryLineItem) => string): [string, DxSummaryLineItem[]][] {
    const groups = new Map<string, DxSummaryLineItem[]>();

    items.forEach(item => {
      const key = selector(item);
      const existingItems = groups.get(key);

      if (existingItems == null) {
        groups.set(key, [item]);
        return;
      }

      existingItems.push(item);
    });

    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }

  private getTotals(items: DxSummaryLineItem[]): DxSummaryTotals {
    return items.reduce((totals, item) => {
      totals.hccCount += item.HCCCount;
      totals.dxCount += item.DxCount;
      return totals;
    }, { dxCount: 0, hccCount: 0 });
  }

  private getDisplayLabel(value: string, fallback: string): string {
    const trimmedValue = value?.trim();
    return trimmedValue ? trimmedValue : fallback;
  }

  private isOpenStateValue(value: string): boolean {
    return value?.trim().toLowerCase() === 'open';
  }

  private isPendingStateValue(value: string): boolean {
    return value?.trim().toLowerCase() === 'pending';
  }

  private isRootCauseSuppressedState(value: string): boolean {
    return this.isOpenStateValue(value) || this.isPendingStateValue(value);
  }

  private getQueryFilters(): IQueryFilterRow[] {
    return [
      {
        label: 'Service Year',
        value: this.lastGeneratedServiceYear.toString(),
        type: 'String'
      },
      {
        label: 'Payer',
        value: this.getLastGeneratedPayerLabel(),
        type: 'String'
      }
    ];
  }

  private getLastGeneratedPayerLabel(): string {
    if (this.lastGeneratedPayerIDs.length === 0) {
      return 'All';
    }

    const matchedPayers = this.lastGeneratedPayerIDs
      .map(id => this.payerOptions.find(option => Number(option?.ID) === id))
      .filter((payer): payer is PayerItem => !!payer);

    if (matchedPayers.length === 0) {
      return this.lastGeneratedPayerIDs.join(', ');
    }

    return matchedPayers
      .map(payer => payer.Abbreviation || payer.Name || String(payer.ID))
      .join(', ');
  }

}
